import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Formsy from 'formsy-react';
import { Map, fromJS } from 'immutable';
import React from 'react';
import { withTranslation } from 'react-i18next';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import PasswordBox from '@sophocles/components/FormFields/PasswordBox';
import PasswordStrengthReport from '@sophocles/components/PasswordStrengthReport';
// eslint-disable-next-line no-restricted-imports -- genuine warning, not a destructive confirm
import WarningDialog from '@sophocles/components/WarningDialog';
import { useLazyRetrieveUserQuery, useSetUserPasswordMutation } from '@sophocles/features/entities/users';
import { withRouter } from '@sophocles/router/hoc';
import { getPasswordVerifier } from '@sophocles/utils';

const styles = {
  headline: {
    fontSize: 18,
    marginBottom: 10,
    fontWeight: 500,
  },
};

class EditPasswordPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      canSubmit: false,
      user: Map(),
      warning: false,
      password: '',
      confirmationPassword: '',
    };
  }

  componentDidMount() {
    const {
      match: { params },
      fetchUser,
    } = this.props;
    fetchUser(params.user_id).then(res => this.setState({ user: fromJS(res) }));
  }

  enableSubmit = () => this.setState({ canSubmit: true });

  disableSubmit = () => this.setState({ canSubmit: false });

  openWarning = () => this.setState({ warning: true });

  closeWarning = () => this.setState({ warning: false });

  savePassword = values => {
    // do the save
    const { setPassword, router, location } = this.props;
    const { user } = this.state;

    const userId = user.get('id', 0);
    const userPath = location.pathname.split('manualpassword')[0];

    if (values.password !== values.password_2) {
      return alert('Password and Confirm Password do not match');
    }

    setPassword(userId, values.password).then(res => {
      res.error ? alert('Something went wrong.  This operation failed.') : router.navigate(userPath, { replace: true });
    });
  };

  submitForm = () => this.passwordForm.submit();

  render() {
    const { t } = this.props;
    const { canSubmit, user, warning } = this.state;

    const userId = user.get('id', '');
    const firstName = user.get('first_name', '');
    const lastName = user.get('last_name', '');

    const verifyPassword = getPasswordVerifier();
    let isPasswordValid = true;
    const passwordStatus = verifyPassword(this.state.password);
    if (passwordStatus) {
      isPasswordValid = passwordStatus.isValid;
    }

    const passwordsMatch = this.state.password === this.state.confirmationPassword;

    const pageTitle = t('userSettings.manualPasswordAssignment');

    return (
      <AppNav page="support_users" notFullHeight={true} {...this.props}>
        <div className="container-fluid" style={{ top: '64px', height: '100%', margin: '0 16px' }}>
          <TitleBar showBackButton title={pageTitle} />

          <Paper style={{ marginBottom: '16px', padding: '16px' }}>
            <div style={{ paddingBottom: '16px' }}>
              <div style={{ display: 'flex' }}>
                <div style={{ flex: '0 0 250px' }}>
                  <h4 style={styles.headline}>{t('userSettings.changePassword')}</h4>
                  <p>{t('userSettings.passwordRequirements')}</p>
                  <PasswordStrengthReport textAlign="left" report={passwordStatus.metrics} />
                </div>

                <div style={{ flex: '1 1 auto', paddingLeft: '16px' }}>
                  <div>
                    {t('users.userDetails', 'User Details:')}
                    <ul>
                      <li>ID: {userId}</li>
                      <li>First Name: {firstName}</li>
                      <li>Last Name: {lastName}</li>
                      <li>Email: {user.get('email', '')}</li>
                      <li>
                        Customer: {user.getIn(['customer', 'id'], '')}. {user.getIn(['customer', 'name'], '')}
                      </li>
                    </ul>
                  </div>
                  <Formsy
                    ref={input => (this.passwordForm = input)}
                    onSubmit={this.savePassword}
                    onValid={this.enableSubmit}
                    onInvalid={this.disableSubmit}
                    className=""
                  >
                    <div style={{ padding: '0 16px' }}>
                      <PasswordBox
                        value={this.state.password}
                        passValue={value => this.setState({ password: value })}
                        name="password"
                        title={t('userSettings.password')}
                        required={true}
                        fullWidth={true}
                        validations={{
                          goodPassword: (values, value) => {
                            return verifyPassword(value).isValid
                              ? true
                              : 'Password does not meet complexity requirements.';
                          },
                        }}
                      />
                      <div style={{ height: '16px' }} />
                      <PasswordBox
                        value={this.state.confirmationPassword}
                        passValue={value => this.setState({ confirmationPassword: value })}
                        name="password_2"
                        title={t('userSettings.confirmPassword')}
                        fullWidth={true}
                        validations={{
                          isSame: (values, value) =>
                            value == values.password ? true : 'Must be the same as the Password field above.',
                        }}
                      />
                      {!passwordsMatch ? (
                        <Typography variant="caption" color="error" sx={{ mt: 2 }}>
                          {t('users.passwordsMismatch', 'Passwords provided do not match')}
                        </Typography>
                      ) : null}
                    </div>
                  </Formsy>
                  {warning && (
                    <WarningDialog
                      open={warning}
                      title="Are you sure?"
                      body={
                        <div>
                          <p>
                            {t('users.aboutToSetPassword', 'You are about to set a password for:')}
                            <br />{' '}
                            <b>
                              {userId}. {firstName} {lastName}
                            </b>
                          </p>
                          <p>
                            There will be no notice sent to this user, so make sure you notify the user of their new
                            password. Hopefully this was done at their request.
                          </p>
                        </div>
                      }
                      onConfirm={this.submitForm}
                      onCancel={this.closeWarning}
                    />
                  )}
                </div>
              </div>
            </div>

            <div style={{ paddingBottom: '16px' }}>
              <div style={{ display: 'flex' }}>
                <div style={{ flex: '0 0 250px' }} />

                <div style={{ flex: '1 1 auto', paddingLeft: '16px' }}>
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!(canSubmit && isPasswordValid && passwordsMatch)}
                      onClick={this.openWarning}
                    >
                      {t('userSettings.saveNewPassword')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Paper>
        </div>
      </AppNav>
    );
  }
}

// Thin functional bridge: supplies the odl-app user read/set-password triggers
// as props with the legacy thunk signatures so the class body stays untouched.
// `setPassword` returns the non-unwrapped trigger result ({ data } | { error })
// so the class's `res.error` check still works.
function EditPasswordPageWithUserMutations(props) {
  const [retrieveUser] = useLazyRetrieveUserQuery();
  const [setUserPassword] = useSetUserPasswordMutation();

  const fetchUser = async userId => {
    try {
      return await retrieveUser({ id: userId }).unwrap();
    } catch {
      return {};
    }
  };
  const setPassword = (userId, password) => setUserPassword({ id: userId, password });

  return <EditPasswordPage {...props} fetchUser={fetchUser} setPassword={setPassword} />;
}

const EditPasswordView = withRouter(withTranslation()(EditPasswordPageWithUserMutations));

export default EditPasswordView;
